import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

import { EnumUserRole } from '../../lib/graphql';
import { BaseModule, ModuleRouteCreator } from 'lib/modules';

// Lazy-loaded page components
const EmployeeReportingDashboardPage = lazy(() =>
  import(
    /* webpackChunkName: "employee-reporting" */ 'features/reporting/pages/employee-reporting-dashboard/employee-reporting-dashboard-page'
  ).then((m) => ({ default: m.EmployeeReportingDashboardPage })),
);

const OwnerReportingDashboardPage = lazy(() =>
  import(
    /* webpackChunkName: "owner-reporting" */ 'features/reporting/pages/owner-reporting-dashboard/owner-reporting-dashboard-page'
  ).then((m) => ({ default: m.OwnerReportingDashboardPage })),
);

const ForecastingPage = lazy(() =>
  import(
    /* webpackChunkName: "forecasting" */ 'features/reporting/pages/forecasting/forecasting-page'
  ).then((m) => ({ default: m.ForecastingPage })),
);

const FinancialReportingConfigPage = lazy(
  () =>
    import(
      /* webpackChunkName: "financial-reporting-config" */ 'features/reporting/pages/financial-reporting-config/financial-reporting-config-page'
    ),
);

const ProcessorWorkflowFunnelPage = lazy(() =>
  import(
    /* webpackChunkName: "workflow-funnel" */ 'features/reporting/pages/processor-workflow-funnel/processor-workflow-funnel-page'
  ).then((m) => ({ default: m.ProcessorWorkflowFunnelPage })),
);

/**
 * Reporting Module Implementation
 *
 * Peer benchmarks used to live here; it now has its own module
 * (`features/reporting/peer-benchmarks-module`) so it can be rolled out
 * independently of the rest of reporting.
 */
class ReportingModuleClass extends BaseModule {
  id = 'REPORTING';
  name = 'Reporting';
  label = 'Reporting & Analytics';
  description = 'Processor reporting, analytics, and forecasting';
  version = '1.0.0';
  /**
   * Creates route objects for the Reporting module
   * @param ProtectedRoute
   * @param LazyWrapper
   * @param settings
   * @param ModuleProtectedRoute
   */
  createRoutes: ModuleRouteCreator = (
    ProtectedRoute,
    LazyWrapper,
    settings,
    ModuleProtectedRoute,
  ): RouteObject[] => {
    const routes: RouteObject[] = [];
    const RouteProtector = ModuleProtectedRoute || ProtectedRoute;

    // Operations reporting (Vendor)
    routes.push({
      path: '/processor/reporting',
      element: (
        <RouteProtector
          component={EmployeeReportingDashboardPage}
          moduleId={this.id}
          allowedUserRoles={[EnumUserRole.Vendor]}
        />
      ),
    });

    // Revenue reporting (VendorOwner/Admin)
    routes.push({
      path: '/processor/owner',
      element: (
        <RouteProtector
          component={OwnerReportingDashboardPage}
          moduleId={this.id}
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    // Workflow funnel analysis (VendorOwner/Admin)
    routes.push({
      path: '/processor/funnel',
      element: (
        <RouteProtector
          component={ProcessorWorkflowFunnelPage}
          moduleId="REPORTING"
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    // Forecasting
    routes.push({
      path: '/processor/forecasting',
      element: (
        <RouteProtector
          component={ForecastingPage}
          moduleId={this.id}
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    // Reporting configuration
    routes.push({
      path: '/processor/reporting-config',
      element: (
        <RouteProtector
          component={FinancialReportingConfigPage}
          moduleId={this.id}
          allowedUserRoles={[EnumUserRole.VendorOwner, EnumUserRole.Admin]}
        />
      ),
    });

    return routes;
  };
}

// Export singleton instance
export const ReportingModule = new ReportingModuleClass();
